import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/company/ImageUpload";
import { CompanyLogo } from "@/components/company/CompanyLogo";
import { useI18n } from "@/lib/i18n/i18n";
import { appCompany } from "@/lib/demo/companies";

const JOB_TYPES = ["Prácticas", "Empleo", "Programa graduados"];
export const NEW_OFFER = { title: "", type: "Empleo", area: "", location: "", titulaciones: [] };

export const BrandSection = ({ form, set }) => {
  const { t } = useI18n();
  return (
    <>
      <div className="grid gap-5 sm:grid-cols-[1fr_1.6fr]">
        <ImageUpload id="logo" label="Logotipo" hint="PNG, JPG o SVG · fondo transparente recomendado" value={form.logoImg} onChange={set("logoImg")} max={480} type="image/png" fit="contain" />
        <ImageUpload id="cover" label="Imagen de portada" hint="JPG o PNG · formato horizontal" value={form.cover} onChange={set("cover")} max={1600} type="image/jpeg" fit="cover" />
      </div>
      <label className="mt-5 flex cursor-pointer items-center gap-4 rounded-2xl bg-slate-50 p-4">
        <input type="color" value={form.brandColor} onChange={(e) => set("brandColor")(e.target.value)} className="h-11 w-14 shrink-0 cursor-pointer rounded-lg border border-slate-200 bg-white p-1" data-testid="company-reg-brand-color" />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold uppercase tracking-wider text-slate-600">{t("Color de marca")}</span>
          <span className="block text-xs text-slate-500">{t("Se usa en el logotipo tipográfico si no subes un logotipo.")}</span>
        </span>
        {!form.logoImg && form.name && <span className="hidden sm:block"><CompanyLogo id="brand-sample" logo={appCompany(form).logo} size="text-xl" /></span>}
      </label>
    </>
  );
};

const OfferCard = ({ o, i, titulaciones, patch, onRemove }) => {
  const { t } = useI18n();
  const tits = o.titulaciones || [];
  const toggle = (x) => patch({ titulaciones: tits.includes(x) ? tits.filter((y) => y !== x) : [...tits, x] });
  return (
    <div className="rounded-2xl border border-slate-200 p-4 sm:p-5" data-testid={`company-reg-offer-${i}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Oferta {n}", { n: i + 1 })}</span>
        {onRemove && <button type="button" onClick={onRemove} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-slate-900" data-testid={`company-reg-offer-remove-${i}`}><X className="h-4 w-4" /></button>}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Input value={o.title} onChange={(e) => patch({ title: e.target.value })} placeholder={t("Puesto")} className="h-12 rounded-xl sm:col-span-2" data-testid={`company-reg-offer-title-${i}`} />
        <Select value={o.type} onValueChange={(v) => patch({ type: v })}>
          <SelectTrigger className="h-12 rounded-xl" data-testid={`company-reg-offer-type-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{JOB_TYPES.map((j) => <SelectItem key={j} value={j}>{t(j)}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={o.area || ""} onChange={(e) => patch({ area: e.target.value })} placeholder={t("Área (p. ej. Tecnología)")} className="h-12 rounded-xl" data-testid={`company-reg-offer-area-${i}`} />
        <Input value={o.location || ""} onChange={(e) => patch({ location: e.target.value })} placeholder={t("Ubicación (p. ej. València)")} className="h-12 rounded-xl sm:col-span-2" data-testid={`company-reg-offer-location-${i}`} />
      </div>
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("Titulaciones de la oferta")}</p>
      {titulaciones.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {titulaciones.map((x) => (
            <button key={x} type="button" onClick={() => toggle(x)} className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${tits.includes(x) ? "border-upv bg-upv text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`} data-testid={`company-reg-offer-${i}-tit-${x}`}>{t(x)}</button>
          ))}
        </div>
      ) : <p className="mt-2 text-xs text-slate-400">{t("Selecciona primero las titulaciones buscadas.")}</p>}
    </div>
  );
};

export const OffersEditor = ({ offers, titulaciones, onChange }) => {
  const { t } = useI18n();
  return (
    <div className="mt-3 space-y-4">
      {offers.map((o, i) => (
        <OfferCard
          key={i} o={o} i={i} titulaciones={titulaciones}
          patch={(p) => onChange(offers.map((x, j) => (j === i ? { ...x, ...p } : x)))}
          onRemove={offers.length > 1 ? () => onChange(offers.filter((_, j) => j !== i)) : null}
        />
      ))}
      {offers.length < 3 && <button type="button" onClick={() => onChange([...offers, NEW_OFFER])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-upv" data-testid="company-reg-add-offer-button"><Plus className="h-4 w-4" /> {t("Añadir oferta")}</button>}
    </div>
  );
};
